import { useRequireAuth } from "lib/use-auth";
import React, { useEffect, useState } from "react";
import { PencilIcon, XIcon } from "@heroicons/react/outline";
import Button from "@library/Button";
import { Evaluator, HumanEvaluator } from "@/types/app/evaluator";
import { EvaluatorLog, Log } from "@/types/app/log";
import { createEvaluatorLogAndFetchLog } from "@/services/evaluators.service";
import { None } from "@components/llama/Tables/Logs/LogCells";
import TruncateMarkup from "react-truncate-markup";
import { COMBOBOX_DATA_TESTID_TAG } from "@library/Combobox";

export const FEEDBACK_TEXT_DATA_TESTID_TAG = "TAG_TO_FIND_FEEDBACK_TEXT";
export const FEEDBACK_TEXTAREA_DATA_TESTID_TAG = "TAG_TO_FIND_FEEDBACK_TEXTAREA";

interface Props {
  evaluator: Evaluator;
  latestFeedback: Log | null;
  setAddingFeedback: (addingFeedback: boolean) => void;
  onError: (error: string) => void;
  onAddFeedback: (evaluatorLog: EvaluatorLog) => void;
  placeholder?: string;
  log: Log;
}

const FeedbackText = ({
  evaluator,
  latestFeedback,
  setAddingFeedback,
  onAddFeedback,
  onError,
  log,
  placeholder,
}: Props) => {
  const { user } = useRequireAuth();

  const judgment =
    latestFeedback && "judgment" in latestFeedback && latestFeedback.judgment ? latestFeedback.judgment : null;
  const [textValue, setTextValue] = useState(judgment);

  return (
    <EditableTextBoxWithSave
      value={textValue}
      placeholder={placeholder ?? "None"}
      setValue={async (value) => {
        setAddingFeedback(true);
        try {
          setTextValue(value);
          const createdLog = await createEvaluatorLogAndFetchLog(
            {
              parent_id: log.id,
              id: evaluator.id,
              judgment: value === "" ? null : value,
              user: user?.email_address,
            },
            evaluator.version_id,
          );

          onAddFeedback(createdLog);
        } catch (error) {
          onError("Failed to update field. Please try again");
          setTextValue(judgment);
        } finally {
          setAddingFeedback(false);
        }
      }}
    />
  );
};

export default FeedbackText;

export const FeedbackCorrection = ({
  evaluator,
  latestFeedback,
  setAddingFeedback,
  onAddFeedback,
  placeholder,
  onError,
  log,
}: Props) => {
  const { user } = useRequireAuth();

  const judgment =
    latestFeedback && "judgment" in latestFeedback && latestFeedback.judgment ? latestFeedback.judgment : null;
  const [textValue, setTextValue] = useState(judgment);
  return (
    <EditableTextBoxWithSave
      value={textValue}
      placeholder={placeholder ?? "None"}
      defaultValue={judgment || log.output || undefined}
      setValue={async (value) => {
        setAddingFeedback(true);
        try {
          setTextValue(value);
          const createdLog = await createEvaluatorLogAndFetchLog(
            {
              id: evaluator.id,
              parent_id: log.id,
              judgment: value === "" ? null : value,
              user: user?.email_address,
            },
            evaluator.version_id,
          );

          onAddFeedback(createdLog);
        } catch (error) {
          onError("Failed to update field. Please try again");
          setTextValue(judgment);
        } finally {
          setAddingFeedback(false);
        }
      }}
    />
  );
};

interface TextBoxProps {
  value: string;
  setValue: (value: string) => void;
  defaultValue?: string;
  placeholder?: string;
}

const EditableTextBoxWithSave = ({ value, setValue, defaultValue, placeholder }: TextBoxProps) => {
  const [displayedValue, setDisplayedValue] = useState(value);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDisplayedValue(value);
  }, [value]);

  if (editing) {
    return (
      <div className="flex-grow">
        {/* Negative margin on div = padding on textarea + 1 to account for border */}
        <textarea
          ref={textareaRef}
          data-testid={FEEDBACK_TEXTAREA_DATA_TESTID_TAG}
          className="h-80px w-full flex-grow whitespace-pre-wrap rounded-sm p-[3px] text-13-18 text-oldgray-800"
          autoFocus
          defaultValue={displayedValue}
          onFocus={() => {
            // TODO persist non save work between changes of focus
            setDisplayedValue(value);
            setEditing(true);
            setEdited(false);
          }}
          onChange={(event) => {
            value === event.target.value ? setEdited(false) : setEdited(true);
            setDisplayedValue(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && event.metaKey) {
              if (edited) {
                setValue(displayedValue);
              }
              setEdited(false);
              setEditing(false);
            }
            if (event.key === "Escape") {
              setDisplayedValue(value);
              setEditing(false);
              event.stopPropagation();
              event.preventDefault();
            }
            if (event.key === "Enter" || event.key === "Backspace" || event.key === "Delete") {
              event.stopPropagation();
            }
          }}
        />

        <div className="flex justify-end gap-12">
          <Button
            size={24}
            styling={"ghost"}
            onClick={() => {
              // Cancel
              setEditing(false);
              setDisplayedValue(value);
            }}
            className="min-w-0"
          >
            Cancel
          </Button>
          <Button
            size={24}
            shade={edited ? "blue" : "gray"}
            styling={edited ? "solid" : "outline"}
            disabled={!edited}
            disableFocus
            onClick={() => {
              setValue(displayedValue);
              setEdited(false);
              setEditing(false);
            }}
            className="min-w-0"
          >
            Save
          </Button>
        </div>
        {/* {JSON.stringify({ value, displayedValue, defaultValue })} */}
      </div>
    );
  }

  return (
    <span
      data-testid={FEEDBACK_TEXT_DATA_TESTID_TAG}
      className="group relative w-full min-w-[200px] cursor-pointer rounded-sm pb-4 pr-4 pt-4 hover:bg-oldgray-200"
      onClick={() => {
        setEditing(true);
        setTimeout(() => {
          // wait for (magic) 30 ms for the textarea to be rendered and then focus.
          textareaRef.current?.focus();
        }, 30);
      }}
    >
      <div className="flex max-w-[542px] justify-start whitespace-pre-wrap pl-2 pr-20 text-13-18 text-oldgray-800">
        {!displayedValue ? (
          <div className="font-mono text-12-14 text-text-base-4">{placeholder ?? "None"}</div>
        ) : (
          <TruncateMarkup
            lines={3}
            ellipsis={
              <span onClick={() => setExpanded(true)} className="text-blue cursor-pointer">
                {" "}
                ...
              </span>
            }
          >
            <span>{displayedValue}</span>
          </TruncateMarkup>
        )}
      </div>
      <div className="absolute inset-y-4 right-2 flex opacity-50 group-hover:opacity-100">
        <PencilIcon className="h-16 w-16 text-oldgray-600" />
      </div>
    </span>
  );
};
